import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  getPublicAuthHeader,
  getReturnWidgetFetchClient,
} from "./return-widget-client";

export type ComputeReturnFeesLineItem = {
  lineItemId: string;
  quantity: number;
  reasonId?: string;
  resolution: string;
};

export type ComputeReturnFeesBody = {
  providerId: string;
  orderId: string;
  lineItems: ComputeReturnFeesLineItem[];
  isReturnToStore?: boolean;
};

export type ComputeReturnFeesResponse = {
  /** Fee for the exact basket as provided (resolutions respected). */
  current: number;
  /** Per-resolution hypothetical fee. `null` means shop opted out (fee = 0). */
  byResolution: {
    refund: number;
    discount: number | null;
    exchange: number;
    exchangeCredit: number;
  };
  /**
   * Shopify tax already included in the matching fee (BBS-366, US orders).
   * Absent from a backend older than the field.
   */
  tax?: {
    current: number;
    refund: number;
    discount: number | null;
  };
  currency: string;
  /**
   * `OVERRIDE_STORE_RETURN` evaluated on this basket, as the creation will:
   * `false` = a rule forbids the return-to-store. Catches rules keyed on the
   * return intent (resolutions, reasons…) that `opt.isStoreReturnAvailable`,
   * computed at order load, cannot see (BBS-418).
   */
  isStoreReturnAvailable?: boolean | null;
};

export async function publicComputeReturnFees(
  body: ComputeReturnFeesBody,
): Promise<ComputeReturnFeesResponse> {
  const { data, error } = await getReturnWidgetFetchClient().POST(
    "/public/orders/compute-return-fees",
    { params: { header: getPublicAuthHeader() }, body: body as never },
  );
  if (error) throw error;
  return data as ComputeReturnFeesResponse;
}

export function usePublicComputeReturnFees(body: ComputeReturnFeesBody | null) {
  const queryKey = [
    "compute-return-fees",
    body?.providerId ?? "",
    body?.orderId ?? "",
    (body?.lineItems ?? [])
      .map(
        (li) =>
          `${li.lineItemId}:${li.quantity}:${li.resolution}:${li.reasonId ?? ""}`,
      )
      .sort()
      .join("|"),
    body?.isReturnToStore ? "rts" : "carrier",
  ];

  return useQuery({
    queryKey,
    queryFn: () => {
      if (!body) throw new Error("Missing body for compute-return-fees");
      return publicComputeReturnFees(body);
    },
    enabled: !!body,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 30,
    retry: 1,
  });
}
